import { alpha, Box, IconButton, styled, Tooltip, Typography } from '@mui/material';
import { ExpandLess, ExpandMore } from '@mui/icons-material';
import { Asset, MatchScoresResponse } from '@/api/generatedApi';
import { ComparePageObjectModel } from '@/poms/comparePageObjectModel';
import { getAssetName } from '@/utils/assets';
import { formatMatchPercentage } from '@/utils/format';
import { useSessionState } from '@/hooks/useSessionState';

const MATCH_SCORES_EXPANDED_STORAGE_KEY = 'compareMatchScoresExpanded';

type GeometricMatchScores = MatchScoresResponse['geometric'];
type VolumetricMatchScores = MatchScoresResponse['volumetric'];

const MatchPercentageContainer = styled(Box)(({ theme }) => ({
  position: 'relative',
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(1),
  padding: theme.spacing(1.5),
  backgroundColor: theme.palette.background.paper,
  borderBottom: `1px solid ${theme.palette.divider}`,
}));

const ToggleButton = styled(IconButton)(({ theme }) => ({
  position: 'absolute',
  top: theme.spacing(0.5),
  right: theme.spacing(0.5),
}));

const PercentageBar = styled(Box)(({ theme }) => ({
  position: 'relative',
  height: 24,
  backgroundColor: theme.palette.divider,
  borderRadius: 12,
  overflow: 'hidden',
  marginTop: theme.spacing(0.5),
}));

const PercentageFill = styled(Box)(({ theme }) => ({
  height: '100%',
  backgroundColor: theme.palette.secondary.main,
  transition: 'width 0.3s ease',
  borderRadius: 12,
}));

const CollapsedScores = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexWrap: 'wrap',
  columnGap: theme.spacing(1.5),
  rowGap: theme.spacing(0.5),
  paddingRight: theme.spacing(4),
}));

const CollapsedScore = styled(Typography)({
  whiteSpace: 'nowrap',
});

const PercentageText = styled(Typography)(({ theme }) => ({
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  fontWeight: 600,
  fontSize: '0.875rem',
  color: theme.palette.common.white,
  textShadow: `0 0 4px ${alpha(theme.palette.secondary.main, 0.8)}`,
  zIndex: 1,
}));

const ScoreBar = ({
  label,
  description,
  matchPercentage,
  testId,
}: {
  label: string;
  description: string;
  matchPercentage: number;
  testId: ComparePageObjectModel;
}) => (
  <Tooltip title={description}>
    <Box data-testid={testId}>
      <Typography variant='body2' fontWeight='600' color='text.secondary'>
        {label}
      </Typography>
      <PercentageBar>
        <PercentageFill style={{ width: `${Math.min(matchPercentage, 100)}%` }} />
        <PercentageText>{formatMatchPercentage(matchPercentage)}%</PercentageText>
      </PercentageBar>
    </Box>
  </Tooltip>
);

export const MatchPercentageDisplay = ({
  geometricScores,
  volumetricScores,
  asset1,
  asset2,
}: {
  geometricScores?: GeometricMatchScores;
  volumetricScores?: VolumetricMatchScores;
  asset1?: Asset;
  asset2?: Asset;
}) => {
  const [isExpanded, setIsExpanded] = useSessionState(MATCH_SCORES_EXPANDED_STORAGE_KEY, true);

  if (!geometricScores) return;

  const asset1Name = asset1 ? getAssetName(asset1) : 'Asset 1';
  const asset2Name = asset2 ? getAssetName(asset2) : 'Asset 2';

  return (
    <MatchPercentageContainer>
      {isExpanded ? (
        <>
          <ScoreBar
            label='Geometric Match'
            description={`Overall geometric similarity between ${asset1Name} and ${asset2Name}`}
            matchPercentage={geometricScores.matchPercentage}
            testId={ComparePageObjectModel.GeometricMatchScore}
          />
          {volumetricScores && (
            <ScoreBar
              label='Volumetric Match'
              description={`How much of the volume of ${asset1Name} is contained in ${asset2Name}. This is directional, so comparing in the opposite order can give a different score.`}
              matchPercentage={volumetricScores.matchPercentage}
              testId={ComparePageObjectModel.VolumetricMatchScore}
            />
          )}
        </>
      ) : (
        <CollapsedScores>
          <CollapsedScore variant='body2' fontWeight='600' color='text.secondary'>
            Geometric {formatMatchPercentage(geometricScores.matchPercentage)}%
          </CollapsedScore>
          {volumetricScores && (
            <CollapsedScore variant='body2' fontWeight='600' color='text.secondary'>
              Volumetric {formatMatchPercentage(volumetricScores.matchPercentage)}%
            </CollapsedScore>
          )}
        </CollapsedScores>
      )}
      <ToggleButton
        size='small'
        aria-label={isExpanded ? 'Collapse match scores' : 'Expand match scores'}
        aria-expanded={isExpanded}
        onClick={() => setIsExpanded(expanded => !expanded)}
      >
        {isExpanded ? <ExpandLess fontSize='small' /> : <ExpandMore fontSize='small' />}
      </ToggleButton>
    </MatchPercentageContainer>
  );
};
